import { createElement, Fragment, type ReactNode } from 'react'
import type { GraphNode } from '@/api/graph'
import { parseBlocks, type Block, type InlineToken } from '@/lib/markdown'

/**
 * Render the Familiar's Markdown artifact as React elements.
 *
 * Every string reaches the DOM as an escaped text child — there is no HTML string anywhere, so an
 * artifact cannot inject markup. `[[citation]]` tokens resolve against the live graph exactly as
 * the reader's inline rule always has (see `citations.tsx`): a hit becomes a clickable node, a
 * miss degrades to plain text rather than a dead link.
 */

type CiteResolver = {
  byName: Map<string, GraphNode>
  // The clicked element is handed back so the caller can anchor a popover (the EntityPeek) to it.
  onPick: (n: GraphNode, anchor: HTMLElement) => void
}

function renderInline(tokens: InlineToken[], cite: CiteResolver, keyBase: string): ReactNode[] {
  return tokens.map((tok, idx) => {
    const key = `${keyBase}-${idx}`
    switch (tok.kind) {
      case 'text':
        return <Fragment key={key}>{tok.value}</Fragment>
      case 'code':
        return (
          <code key={key} className="dw-md-code">
            {tok.value}
          </code>
        )
      case 'strong':
        return <strong key={key}>{renderInline(tok.children, cite, key)}</strong>
      case 'em':
        return <em key={key}>{renderInline(tok.children, cite, key)}</em>
      case 'cite': {
        const node = cite.byName.get(tok.value.toLowerCase())
        if (!node) {
          // The familiar named something the graph doesn't hold. Show the name, not the brackets.
          return <Fragment key={key}>{tok.value}</Fragment>
        }
        return (
          <button
            key={key}
            type="button"
            className="dw-cite"
            onClick={(e) => cite.onPick(node, e.currentTarget)}
            title={`Quick look at ${node.name}`}
          >
            {tok.value}
          </button>
        )
      }
    }
  })
}

function renderBlock(block: Block, cite: CiteResolver, key: string): ReactNode {
  switch (block.type) {
    case 'heading':
      return createElement(
        `h${Math.min(block.level, 6)}`,
        { key, className: `dw-md-h dw-md-h${block.level}` },
        renderInline(block.inline, cite, key),
      )
    case 'paragraph':
      return (
        <p key={key} className="dw-md-p">
          {renderInline(block.inline, cite, key)}
        </p>
      )
    case 'code':
      return (
        <pre key={key} className="dw-md-pre">
          <code>{block.value}</code>
        </pre>
      )
    case 'blockquote':
      return (
        <blockquote key={key} className="dw-md-quote">
          {renderInline(block.inline, cite, key)}
        </blockquote>
      )
    case 'hr':
      return <hr key={key} className="dw-md-hr" />
    case 'list': {
      const items = block.items.map((item, idx) => (
        <li key={`${key}-${idx}`}>{renderInline(item, cite, `${key}-${idx}`)}</li>
      ))
      return block.ordered ? (
        <ol key={key} className="dw-md-list">
          {items}
        </ol>
      ) : (
        <ul key={key} className="dw-md-list">
          {items}
        </ul>
      )
    }
    case 'table':
      return (
        <div key={key} className="dw-md-table-wrap">
          <table className="dw-md-table">
            <thead>
              <tr>
                {block.header.map((cell, ci) => (
                  <th key={ci}>{renderInline(cell, cite, `${key}-h${ci}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci}>{renderInline(cell, cite, `${key}-${ri}-${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
  }
}

export function Markdown({
  text,
  byName,
  onPick,
}: {
  text: string
  byName: Map<string, GraphNode>
  onPick: (n: GraphNode, anchor: HTMLElement) => void
}) {
  const cite: CiteResolver = { byName, onPick }
  const blocks = parseBlocks(text)
  return <div className="dw-md">{blocks.map((b, i) => renderBlock(b, cite, `b${i}`))}</div>
}
